import React, { useState } from 'react';
import { X, Sparkles, Palette, CheckCircle2, ShoppingBag, Send } from 'lucide-react';
import { NailShape, NailLength, NailSizePreset, NailSizeMeasurements, Product } from '../types';
import { SHAPE_OPTIONS, LENGTH_OPTIONS, SIZING_PRESETS } from '../data/products';
import { useCart } from '../context/CartContext';
import confetti from 'canvas-confetti';

interface CustomOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CustomOrderModal: React.FC<CustomOrderModalProps> = ({ isOpen, onClose }) => {
  const { addToCart } = useCart();

  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [colorTheme, setColorTheme] = useState('Pastel & Gold Swirls (Signature)');
  const [selectedShape, setSelectedShape] = useState<NailShape>('Almond');
  const [selectedLength, setSelectedLength] = useState<NailLength>('Medium');
  const [selectedSizePreset, setSelectedSizePreset] = useState<NailSizePreset>('S');
  const [designNotes, setDesignNotes] = useState('');
  const [customMeasurements, setCustomMeasurements] = useState<NailSizeMeasurements>({
    thumb: '15mm',
    index: '12mm',
    middle: '13mm',
    ring: '11mm',
    pinky: '8mm',
  });
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) return null;

  const colorThemes = [
    'Pastel & Gold Swirls (Signature)',
    'Gymnastics Leotard Match (Custom Colors)',
    'Milky Pearl & 3D Gold Stars',
    'Y2K Swirls & Chrome Accents',
    'Lavender & Cotton Candy Ombré',
    'Minimalist French with Gold Foil',
    'Completely Unique Custom Concept',
  ];

  const handleAddCustomToCart = (e: React.FormEvent) => {
    e.preventDefault();

    const customProduct: Product = {
      id: `custom-${Date.now()}`,
      name: `Custom Hand-Painted Set (${customerName || 'Bespoke'})`,
      slug: 'custom-bespoke-set',
      price: 30.00,
      category: 'custom',
      categoryLabel: 'Custom Commission',
      description: `Custom commission for ${customerName || 'Client'}. Theme: ${colorTheme}. Notes: ${designNotes || 'None specified'}. Contact: ${customerEmail}`,
      shortDescription: `Custom Theme: ${colorTheme}`,
      images: ['/nails/custom-design-by-kylee.svg'],
      gradientStyle: 'from-[#FDE68A] via-[#F472B6] to-[#A78BFA]',
      accentColor: '#D4AF37',
      defaultShape: selectedShape,
      defaultLength: selectedLength,
      rating: 5.0,
      reviewCount: 1,
      designDetails: [
        `Custom Theme: ${colorTheme}`,
        `Client Notes: ${designNotes || 'Standard signature finish'}`,
        '100% Hand-painted by Kylee',
        'Includes full prep kit'
      ]
    };

    addToCart(
      customProduct,
      selectedShape,
      selectedLength,
      selectedSizePreset,
      selectedSizePreset === 'Custom' ? customMeasurements : undefined,
      1
    );

    confetti({
      particleCount: 80,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#D4AF37', '#F472B6', '#C084FC', '#2DD4BF'],
    });

    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      onClose();
    }, 1500);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-6 animate-in fade-in duration-200">
      <div
        className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl border border-[#EADFCF] p-6 sm:p-8 my-auto max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          aria-label="Close Custom Order Form"
          className="absolute top-4 right-4 z-20 p-2 rounded-full bg-[#FAF5EE] text-[#78716C] hover:text-[#292524] shadow-xs border border-[#E7DEC8] transition-transform hover:scale-105"
        >
          <X className="w-5 h-5" />
        </button>

        {submitted ? (
          <div className="text-center py-12 space-y-4 animate-in zoom-in-95">
            <div className="w-16 h-16 rounded-full bg-[#D1FAE5] text-[#059669] flex items-center justify-center mx-auto text-2xl shadow-md">
              ✓
            </div>
            <h3 className="font-serif text-2xl font-bold text-[#292524]">
              Custom Order Added to Bag!
            </h3>
            <p className="text-xs sm:text-sm text-[#78716C] max-w-md mx-auto">
              Kylee will review your design notes and custom colors. Open your cart to complete checkout whenever you are ready!
            </p>
          </div>
        ) : (
          <form onSubmit={handleAddCustomToCart} className="space-y-6">
            
            {/* Header */}
            <div className="space-y-2 text-center sm:text-left">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FEF08A] text-[#854D0E] text-xs font-bold uppercase tracking-wider border border-[#FDE047]">
                <Palette className="w-3.5 h-3.5 text-[#D97706]" />
                <span>Custom Commission ($30 Flat Rate)</span>
              </div>
              <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#292524]">
                Design Your Dream Nail Set
              </h2>
              <p className="text-xs text-[#78716C]">
                Tell Kylee about your dream colors, theme, or competition leotard colors. She will hand-paint an exclusive set tailored to your hands!
              </p>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-bold text-[#292524]">Your Name *</label>
                <input
                  type="text"
                  required
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="e.g. Jessica"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-[#FAF5EE] border border-[#E7DEC8] focus:border-[#D4AF37] focus:outline-none text-xs text-[#292524]"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-[#292524]">Email Address *</label>
                <input
                  type="email"
                  required
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="name@example.com"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-[#FAF5EE] border border-[#E7DEC8] focus:border-[#D4AF37] focus:outline-none text-xs text-[#292524]"
                />
              </div>
            </div>

            {/* Theme & Palette Selection */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-[#292524]">Color Theme / Style Preference</label>
              <select
                value={colorTheme}
                onChange={(e) => setColorTheme(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl bg-[#FAF5EE] border border-[#E7DEC8] focus:border-[#D4AF37] focus:outline-none text-xs text-[#292524]"
              >
                {colorThemes.map((theme, idx) => (
                  <option key={idx} value={theme}>
                    {theme}
                  </option>
                ))}
              </select>
            </div>

            {/* Shape & Length Pickers */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-[#292524]">Preferred Shape</label>
                <div className="grid grid-cols-3 gap-1.5">
                  {(['Almond', 'Coffin', 'Oval', 'Square', 'Stiletto'] as NailShape[]).map((shape) => (
                    <button
                      key={shape}
                      type="button"
                      onClick={() => setSelectedShape(shape)}
                      className={`py-1.5 px-2 rounded-xl text-center text-xs transition-all ${
                        selectedShape === shape
                          ? 'bg-[#FEF08A] text-[#854D0E] font-bold border border-[#FDE047]'
                          : 'bg-[#FAF5EE] text-[#57534E] border border-[#E7DEC8]'
                      }`}
                    >
                      {shape}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-[#292524]">Preferred Length</label>
                <div className="grid grid-cols-3 gap-1.5">
                  {(['Short', 'Medium', 'Long'] as NailLength[]).map((len) => (
                    <button
                      key={len}
                      type="button"
                      onClick={() => setSelectedLength(len)}
                      className={`py-1.5 px-2 rounded-xl text-center text-xs transition-all ${
                        selectedLength === len
                          ? 'bg-[#FCE7F3] text-[#BE185D] font-bold border border-[#F472B6]'
                          : 'bg-[#FAF5EE] text-[#57534E] border border-[#E7DEC8]'
                      }`}
                    >
                      {len}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Sizing Preset */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-[#292524]">Size Preset</label>
              <div className="grid grid-cols-5 gap-1.5">
                {SIZING_PRESETS.map((preset) => (
                  <button
                    key={preset.name}
                    type="button"
                    onClick={() => setSelectedSizePreset(preset.name as NailSizePreset)}
                    className={`py-1.5 px-2 rounded-xl text-center text-xs transition-all ${
                      selectedSizePreset === preset.name
                        ? 'bg-[#EDE9FE] text-[#6B21A8] font-bold border border-[#C084FC]'
                        : 'bg-[#FAF5EE] text-[#57534E] border border-[#E7DEC8]'
                    }`}
                  >
                    {preset.name}
                  </button>
                ))}
              </div>

              {selectedSizePreset === 'Custom' && (
                <div className="mt-2 p-3 rounded-2xl bg-[#FFFDF9] border border-[#F3C053]/50 space-y-2">
                  <span className="text-[11px] font-bold text-[#854D0E] block">
                    Enter nail width in mm (Thumb to Pinky):
                  </span>
                  <div className="grid grid-cols-5 gap-1.5">
                    {(['thumb', 'index', 'middle', 'ring', 'pinky'] as const).map((finger) => (
                      <div key={finger} className="text-center">
                        <label className="text-[10px] uppercase font-bold text-[#78716C] block capitalize">
                          {finger.slice(0, 3)}
                        </label>
                        <input
                          type="text"
                          value={customMeasurements[finger]}
                          onChange={(e) =>
                            setCustomMeasurements((prev) => ({
                              ...prev,
                              [finger]: e.target.value,
                            }))
                          }
                          className="w-full text-center py-1 px-1 text-xs font-semibold rounded-lg bg-white border border-[#E7DEC8] focus:border-[#D4AF37]"
                          placeholder="mm"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Design Notes & Specifics */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-[#292524]">
                Design Description & Inspiration Notes
              </label>
              <textarea
                rows={3}
                value={designNotes}
                onChange={(e) => setDesignNotes(e.target.value)}
                placeholder="Describe your design idea, colors, specific charms, or details (e.g. 'Matching my purple and teal leotard with gold stars on index fingers')..."
                className="w-full px-3.5 py-2.5 rounded-xl bg-[#FAF5EE] border border-[#E7DEC8] focus:border-[#D4AF37] focus:outline-none text-xs text-[#292524] resize-none"
              />
            </div>

            {/* Submit & Add to Cart */}
            <div className="pt-2">
              <button
                type="submit"
                className="w-full py-4 px-6 rounded-2xl text-sm font-bold text-white gold-shimmer-btn shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 active:scale-98"
              >
                <ShoppingBag className="w-5 h-5" />
                <span>Add Custom Commission to Cart ($30.00)</span>
              </button>
              <p className="text-[11px] text-center text-[#78716C] mt-2">
                Kylee will confirm your design details via email before hand-painting!
              </p>
            </div>

          </form>
        )}

      </div>
    </div>
  );
};
